import { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationPage from './NotificationPage';
import { WelcomeMessage } from '@/contexts/AppDataContext';
import { useTeamApp } from '@/contexts/TeamAppContext';
import { getHttpErrorKind } from '@/utils/httpErrors';
import { redirectToUnauthorize } from '@/utils/authRedirect';

interface ProtectedRouteProps {
    children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { isAuthenticated, login, gromToken } = useAuth();
    const location = useLocation();
    const { isLoading } = useTeamApp();
    const retryTimeoutRef = useRef<number | null>(null);

    // Running in Teams app
    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            login().catch((error) => {
                console.error("Login failed:", error);
                // Avoid false auth errors during rolling deploys (transient 5xx/network).
                const kind = getHttpErrorKind(error);
                if (kind === 'transient') {
                    // Retry once shortly; keep showing the loading page meanwhile.
                    if (retryTimeoutRef.current) {
                        window.clearTimeout(retryTimeoutRef.current);
                        retryTimeoutRef.current = null;
                    }
                    retryTimeoutRef.current = window.setTimeout(() => {
                        login().catch((e) => console.error("Retry login failed:", e));
                    }, 1000);
                    return;
                }

                // Any 401 or 403 → redirect to unauthorize; other errors just log
                if (kind === 'auth' || kind === 'forbidden') {
                    redirectToUnauthorize(location.pathname);
                }
            });
        }
        return () => {
            if (retryTimeoutRef.current) {
                window.clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, [isAuthenticated, login, isLoading, location.pathname]);

    if (!gromToken)
        return <NotificationPage content={WelcomeMessage} enableRotation={true} />;
    else
        return <>{children}</>;
}
